/* eslint-disable no-undef */
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { AttributionMode } from '../app-container/types';
import {
    updateUserPreferences,
    buildAttributionPatch,
    readEdgeAttributionMode,
} from '../../utils/updateUserPreferences';
import { EDGE_RIGHTSHOLDER_ATTRIBUTION } from '../../constants/config';
import { Client } from '../../types/api';

type UseRightsholderAttributionOptions = {
    client?: Client | null;
};

// A corrupt mvc-user must not take the page down: every reader here treats
// unparseable storage as "nothing stored".
const getStoredUser = () => {
    try {
        return JSON.parse(localStorage.getItem('mvc-user') || 'null');
    } catch {
        return null;
    }
};

export const useRightsholderAttribution = (
    options?: UseRightsholderAttributionOptions,
) => {
    const { client } = options || {};
    // Key off the client this hook was given, never off mvc-user.selectedClientId:
    // that key is shared by every tab on this origin, so a second tab opened on
    // another client would otherwise make this one read and write that client's mode.
    const clientId = client?.id;

    // Check if client has permission
    const hasPermission = useMemo(() => {
        if (!client) return false;
        return (
            client.permissions?.includes(EDGE_RIGHTSHOLDER_ATTRIBUTION) ?? false
        );
    }, [client]);

    const getAttributionValue = useCallback((): AttributionMode | null => {
        // If no permission, return null (don't use stored preferences)
        if (!hasPermission || clientId == null) return null;

        // Shared with getUseRightsholderAttribution — the util the query paths
        // use — so the switch position and the requests always agree.
        return readEdgeAttributionMode(
            getStoredUser()?.userPreferences?.rightsholder_attribution,
            clientId,
        ) as AttributionMode | null;
    }, [hasPermission, clientId]);

    const [isLoading, setIsLoading] = useState(false);
    const [rightsholderAttribution, setRightsholderAttribution] =
        useState(getAttributionValue);

    useEffect(() => {
        const newValue = getAttributionValue();
        setRightsholderAttribution(newValue);
    }, [getAttributionValue]);

    // Identifies the client a write was started for, so a response that lands
    // after the rendered client changed cannot be applied to the new one.
    const clientIdRef = useRef(clientId);
    useEffect(() => {
        clientIdRef.current = clientId;
    }, [clientId]);

    // Listen for localStorage changes (from other tabs) and custom events (from same tab)
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'mvc-user' && e.newValue) {
                const newValue = getAttributionValue();
                if (newValue !== rightsholderAttribution) {
                    setRightsholderAttribution(newValue);
                }
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleUserUpdate = (e: CustomEvent) => {
            const newValue = getAttributionValue();
            if (newValue !== rightsholderAttribution) {
                setRightsholderAttribution(newValue);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(
            'mvc-user-updated',
            handleUserUpdate as EventListener,
        );
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(
                'mvc-user-updated',
                handleUserUpdate as EventListener,
            );
        };
    }, [rightsholderAttribution, getAttributionValue]);

    const updateRightsholderAttribution = useCallback(
        async (newAttribution: AttributionMode): Promise<void> => {
            // Don't allow updates if client doesn't have permission
            if (!hasPermission) {
                throw new Error(
                    'Cannot update rightsholder attribution: permission not enabled for this client',
                );
            }
            if (clientId == null) {
                throw new Error(
                    'Cannot update rightsholder attribution: no client selected',
                );
            }

            const storedUser = getStoredUser();
            const userId = storedUser?.id;
            // mvc-user is gone when this tab was logged out from another one;
            // writing anyway would PATCH /users/undefined/preferences.
            if (userId == null) {
                throw new Error('Your session has ended. Please log in again.');
            }

            setIsLoading(true);
            try {
                const userPreferences = storedUser.userPreferences;
                await updateUserPreferences({
                    userId,
                    preferences: {
                        // Only the keys being changed: the API merges per key
                        // (RFC 7396), so a mode set for another client on
                        // another tab or device is not overwritten.
                        rightsholder_attribution: buildAttributionPatch(
                            userPreferences?.rightsholder_attribution,
                            clientId,
                            newAttribution,
                        ),
                    },
                });

                // The write may have landed after the rendered client changed;
                // that tab's mode is none of this call's business.
                if (clientIdRef.current !== clientId) return;

                // Read the mode back rather than assuming the request's value:
                // the stored state is what every query path will send, so if the
                // two disagree the switch must not claim the change took effect.
                const persisted = readEdgeAttributionMode(
                    getStoredUser()?.userPreferences?.rightsholder_attribution,
                    clientId,
                );
                if (persisted !== newAttribution) {
                    throw new Error(
                        'Rightsholder attribution could not be saved. Please try again.',
                    );
                }
                setRightsholderAttribution(persisted);
            } catch (error) {
                console.error(
                    'Error updating rightsholder attribution:',
                    error,
                );
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [hasPermission, clientId],
    );

    return {
        rightsholderAttribution,
        updateRightsholderAttribution,
        isLoading,
        hasPermission,
    };
};
